/**
 * Ceiling — Phase 2. Closed boundary like floors; assembly thickness extends UP from
 * (level elevation + heightOffsetFt) — the offset is the ceiling height above the level
 * (default 8'-0").
 *
 * CEILING CORE DEFINITION round (2026-08-18, Matt): a ceiling is either FINISH-ONLY
 * (the default — a gyp lid on the underside of whatever structure is above; ceilings
 * are modeled separate from the roof assembly since a roof is rarely fully covered
 * in ceiling) or FRAMED with its own joists (wood/TJI — ceilingCore.ts), deriving
 * joist framing like a floor.
 */
import type { CategorySchema } from '../../registry'
import { TEXTURE_PLACEMENT_GROUP } from '../../texturePlacement'
import { INSULATION_PARAM_GROUP } from '../../insulationSpec'
import { JOIST_SPACINGS } from '../../floorFraming'
import { TJI_JOIST_SIZES, WOOD_JOIST_SIZES } from '../../floorCore'
import { CEILING_CORE_TYPES } from '../../ceilingCore'
import { BOX_BEAM_PATTERNS } from '../../boxBeams'

export const ceilingSchema: CategorySchema = {
  id: 'ceiling',
  pickRank: 3,
  pathParamKey: 'boundary',
  label: 'Ceiling',
  usesAssembly: 'ceiling',
  typeParams: {
    groups: [
      {
        label: 'Construction',
        params: [
          {
            key: 'coreType', label: 'Core', kind: 'choice', default: 'none',
            choices: CEILING_CORE_TYPES.map((t) => ({ value: t.value, label: t.label })),
          },
          {
            key: 'woodJoistSize', label: 'Joist Size', kind: 'choice', default: '2x8',
            conditionalOn: { param: 'coreType', equals: 'wood' },
            choices: WOOD_JOIST_SIZES.map((s) => ({ value: s.value, label: `${s.label} — ${s.actualLabel} actual` })),
          },
          {
            key: 'tjiJoistSize', label: 'Joist Size', kind: 'choice', default: '11-7/8',
            conditionalOn: { param: 'coreType', equals: 'tji' },
            choices: TJI_JOIST_SIZES.map((s) => ({ value: s.value, label: s.label })),
          },
        ],
      },
      // INSULATION SCHEDULE round (2026-10-03): the type's insulation spec — feeds the
      // derived, fit-checked insulation schedule on the General Notes sheet (insulation.ts)
      INSULATION_PARAM_GROUP,
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Constraints',
        params: [
          { key: 'boundary', label: 'Boundary', kind: 'path2d', default: null },
          // PHASE SPLIT round: opaque, never palette-rendered/transferred — same
          // treatment as topo's points/breaklines/modifiers (types.ts#PhaseSplitEntry).
          { key: 'phaseSplits', label: 'Phase Splits', kind: 'data', default: null, hidden: true },
          // PHASE AREAS (2026-10-30): closed areas sketched in the outline editor (types.ts#PhaseAreaEntry)
          { key: 'phaseAreas', label: 'Phase Areas', kind: 'data', default: null, hidden: true },
          // PAINTED CEILING FINISHES (2026-09-28): the transition lines and the
          // painted areas between them (Paint Bucket) — see commands/slabFinish.ts
          { key: 'finishSplits', label: 'Finish Transitions', kind: 'data', default: null, hidden: true },
          { key: 'finishAreas', label: 'Finish Areas', kind: 'data', default: null, hidden: true },
          // OUTLINE EDITOR (2026-09-28, Matt: "yes, allow holes"): inner closed loops
          // drawn in Edit Boundary — openings through the slab (Path2D[])
          { key: 'boundaryHoles', label: 'Openings', kind: 'data', default: null, hidden: true },
          // FRAMING ZONES (2026-10-07): framing lines drawn in Edit Boundary, each with
          // its seam beam, and the per-zone joist direction/spacing (types.ts)
          { key: 'framingSplits', label: 'Framing Lines', kind: 'data', default: null, hidden: true },
          { key: 'framingZones', label: 'Framing Zones', kind: 'data', default: null, hidden: true },
          { key: 'heightOffsetFt', label: 'Height Above Level', kind: 'length', default: 8, min: 0, max: 40 },
        ],
      },
      {
        // framed ceilings only (cross-schema conditionalOn) — the floor pattern
        label: 'Framing',
        params: [
          {
            // ASSEMBLY INSULATION (2026-10-14, Matt: "floor, roof, and ceilings to have
            // insulation as instance parameters like walls do"): batt-hatches the CORE band
            // in section (section.ts's assembly band pass), framing members masking it.
            // No 3D effect — the wall `insulation` toggle's exact contract.
            key: 'insulation', label: 'Insulation', kind: 'bool', default: false,
            conditionalOn: { param: 'coreType', equals: ['wood', 'tji', 'metal'] },
          },
          {
            key: 'joistSpacingIn', label: 'Joist Spacing', kind: 'choice', default: '16',
            conditionalOn: { param: 'coreType', equals: ['wood', 'tji'] },
            choices: JOIST_SPACINGS.map((s) => ({ value: String(s), label: `${s}″ o.c.` })),
          },
          {
            key: 'joistDirectionDeg', label: 'Joist Direction', kind: 'number', default: 0,
            min: -360, max: 360, positional: true,
            conditionalOn: { param: 'coreType', equals: ['wood', 'tji'] },
          },
        ],
      },
      {
        // DECORATIVE BOX BEAMS (2026-10-08, kernel boxBeams.ts): derived from these; the
        // only stored geometry is the user's per-beam nudges (boxBeamMoves)
        label: 'Box Beams',
        params: [
          { key: 'boxBeams', label: 'Decorative Box Beams', kind: 'bool', default: false },
          {
            key: 'boxBeamPattern', label: 'Layout', kind: 'choice', default: 'oneWay',
            conditionalOn: { param: 'boxBeams', equals: true },
            choices: BOX_BEAM_PATTERNS.map((p) => ({ value: p.value, label: p.label })),
          },
          { key: 'boxBeamWidthFt', label: 'Beam Width', kind: 'length', default: 8 / 12, min: 3 / 12, max: 3, conditionalOn: { param: 'boxBeams', equals: true } },
          { key: 'boxBeamDepthFt', label: 'Beam Depth', kind: 'length', default: 10 / 12, min: 3 / 12, max: 4, conditionalOn: { param: 'boxBeams', equals: true } },
          { key: 'boxBeamSpacingFt', label: 'Spacing (on center)', kind: 'length', default: 4, min: 1, max: 40, conditionalOn: { param: 'boxBeams', equals: true } },
          {
            key: 'boxBeamCrossSpacingFt', label: 'Cross Spacing', kind: 'length', default: null, optional: true, optionalHint: 'same as spacing', min: 1, max: 40,
            conditionalOn: [{ param: 'boxBeams', equals: true }, { param: 'boxBeamPattern', equals: 'grid' }],
          },
          { key: 'boxBeamDirectionDeg', label: 'Beam Direction', kind: 'number', default: 0, min: -360, max: 360, positional: true, conditionalOn: { param: 'boxBeams', equals: true } },
          // '' = the project's standard box beam finish (Project Settings → Trim & Eaves)
          { key: 'boxBeamMaterial', label: 'Beam Finish', kind: 'material', default: '', conditionalOn: { param: 'boxBeams', equals: true } },
          // the Paint Brush's coat on the beams (a color id)
          { key: 'boxBeamColor', label: 'Beam Color', kind: 'text', default: null, hidden: true },
          // the user's per-beam nudges — positional, never transferred
          { key: 'boxBeamMoves', label: 'Beam Positions', kind: 'data', default: null, hidden: true, positional: true },
        ],
      },
      // ATMOSPHERE VIEW Stage 3: per-element texture shift/rotate (texturePlacement.ts)
      TEXTURE_PLACEMENT_GROUP,
    ],
  },
}
